import { chainDynamicValue, DynamicValue } from "@faedev/dynamic-values";
import { composeClassNames } from "@faedev/editor-utils";
import Link from "next/link";
import { useRouter } from "next/router";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import Heading from "src/components/Heading";
import Paragraph from "src/components/Paragraph";
import Translate from "src/components/Translate";
import constants from "src/constants";
import gutter from "src/functions/gutter";
import onChangeToValue from "src/functions/onChangeToValue";
import translate from "src/functions/translate";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sButtons } from "src/styles/sButtons.module.scss";
import { sFormControl } from "src/styles/sFormControl.module.scss";
import { sFormLabel } from "src/styles/sFormLabel.module.scss";
import { sNote } from "src/styles/sNote.module.scss";
import { DataJoinUsForm } from "src/types/DataJoinUsForm";

type Props = { formData: DynamicValue<DataJoinUsForm> };

function FormJoinUsBasic({ formData }: Props) {
  const { locale: lang } = useRouter();
  return (
    <div>
      <Heading
        type={`h2`}
        title={
          <>
            {`1. `}
            <Translate tKey={`Basic informations`} />
          </>
        }
      />
      <Form.Row>
        <Col sm={4} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Name`} />
            </Form.Label>
            <Form.Control
              className={sFormControl}
              required={true}
              name={`name`}
              id={`name`}
              onChange={function (param2) {
                return chainDynamicValue(formData, "name").change(
                  onChangeToValue(param2)
                );
              }}
              value={chainDynamicValue(formData, "name").value}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate tKey={`Fill in this field, it is mandatory.`} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={4} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Surname`} />
            </Form.Label>
            <Form.Control
              className={sFormControl}
              required={true}
              name={`surname`}
              id={`surname`}
              onChange={function (param2) {
                return chainDynamicValue(formData, "surname").change(
                  onChangeToValue(param2)
                );
              }}
              value={chainDynamicValue(formData, "surname").value}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate tKey={`Fill in this field, it is mandatory.`} />
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Form.Row>
      <Form.Row>
        <Col sm={4} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Email`} />
            </Form.Label>
            <Form.Control
              className={sFormControl}
              required={true}
              name={`email`}
              id={`email`}
              value={chainDynamicValue(formData, "email").value}
              onChange={function (param2) {
                return chainDynamicValue(formData, "email").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate tKey={`Fill in this field, it is mandatory.`} />
            </Form.Control.Feedback>
            <Form.Text className={sNote}>
              <Translate tKey={`The email format is name@domain.tld`} />
            </Form.Text>
          </Form.Group>
        </Col>
        <Col sm={4} xs={12}>
          <Form.Group>
            <Form.Label className={sFormLabel}>
              <Translate tKey={`Phone`} />
            </Form.Label>
            <Form.Control
              className={sFormControl}
              required={true}
              name={`phone`}
              id={`phone`}
              value={chainDynamicValue(formData, "phone").value}
              onChange={function (param2) {
                return chainDynamicValue(formData, "phone").change(
                  onChangeToValue(param2)
                );
              }}
            />
            <Form.Control.Feedback type={`invalid`}>
              <Translate tKey={`Fill in this field, it is mandatory.`} />
            </Form.Control.Feedback>
            <Form.Text className={sNote}>
              <Translate
                tKey={`Enter the phone number, including the country code, eg +420 123 456 789`}
              />
            </Form.Text>
          </Form.Group>
        </Col>
      </Form.Row>
      <div>
        <Form.Label className={sFormLabel}>
          <Translate tKey={`Type of profile`} />
        </Form.Label>
        <Form.Group key={`profile`}>
          <Form.Check inline={true}>
            <Form.Check.Input
              className={sFormControl}
              type={`radio`}
              value={translate(`Instructor`, lang)}
              name={`profile`}
              id={`profile-instructor`}
              required={true}
            />
            <Form.Check.Label
              className={sFormLabel}
              htmlFor={`profile-instructor`}
            >
              <Translate tKey={`Instructor`} />
            </Form.Check.Label>
          </Form.Check>
          <Form.Check inline={true}>
            <Form.Check.Input
              className={sFormControl}
              type={`radio`}
              value={translate(`Studio`, lang)}
              name={`profile`}
              id={`profile-studio`}
              required={true}
            />
            <Form.Check.Label className={sFormLabel} htmlFor={`profile-studio`}>
              <Translate tKey={`Studio`} />
            </Form.Check.Label>
          </Form.Check>
        </Form.Group>
      </div>
      <Row>
        <Col sm={6} xs={12}>
          <Alert
            style={gutter(`margin`, `top`, `xl`)}
            variant={`light`}
            className={composeClassNames([sAlert, "bg-transparent"])}
          >
            <Translate
              tKey={`To create a profile, we will need the information, we ask for in the following form. Not all are required and it's up to you what you want to share on your profile. You can fill in the information you already know. This will help us shorten the time to create your profile. It takes 20-40 minutes to complete the entire form, depending on the extent of the answers. Make sure you have enough time before you start filling it out. If you prefer personal communication, you do not have to fill in the form. Just enter your name, surname, email, phone number and profile type, and our colleague will contact you to set up the profile to your satisfaction.`}
            />
          </Alert>
        </Col>
        <Col sm={6} xs={12}>
          <Alert
            style={gutter(`margin`, `top`, `xl`)}
            variant={`light`}
            className={composeClassNames([sAlert])}
          >
            <Paragraph size={`xn`} width={`semibold`}>
              <Translate
                tKey={`Another option is to download the Word document, fill in the information there and send it back to our e-mail address:`}
              />
              {` `}
              <Link
                href={
                  `mailto:` +
                  (function () {
                    switch (lang) {
                      case `cs`:
                        return constants.Paths.profileEmailCS;
                      case `sk`:
                        return constants.Paths.profileEmailSK;
                      case `en`:
                        return constants.Paths.profileEmailEN;
                      default:
                        return constants.Paths.profileEmailEN;
                    }
                  })()
                }
                passHref
                locale={false}
              >
                <Button
                  type={`button`}
                  variant={`link`}
                  as={`a`}
                  target={`_blank`}
                  className={composeClassNames([sButton, "btn-inline"])}
                >
                  {(function () {
                    switch (lang) {
                      case `cs`:
                        return constants.Paths.profileEmailCS;
                      case `sk`:
                        return constants.Paths.profileEmailSK;
                      case `en`:
                        return constants.Paths.profileEmailEN;
                      default:
                        return constants.Paths.profileEmailEN;
                    }
                  })()}
                </Button>
              </Link>
            </Paragraph>
            <div className={composeClassNames([sButtons])}>
              <Link
                href={constants.Paths.profileDocumentCS}
                passHref
                locale={false}
              >
                <Button
                  type={`button`}
                  variant={`outline-primary`}
                  as={`a`}
                  className={composeClassNames([sButton])}
                  target={`_blank`}
                >
                  <Translate
                    tKey={`Download the Word document to fill in the profile`}
                  />
                </Button>
              </Link>
            </div>
          </Alert>
        </Col>
      </Row>
    </div>
  );
}

export default FormJoinUsBasic;
